import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowDown, ArrowUp, X } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import type { AuthMeResponse } from "@kandoo/shared";
import { api, type BoardCard, type BoardDetail } from "../api/client";
import { LabelChip } from "./LabelChip";
import { Avatar } from "./Avatar";
import { ConfirmButton } from "./ConfirmButton";
import { CommitInput, FieldEditor, useSetFieldValue } from "./CardFields";
import { toast } from "./Toaster";
import { fromDateInputValue, toDateInputValue } from "../util/date";
import { formatFieldValue } from "../util/fields";
import { sortCards, sortLists } from "../util/position";

type SortKey = "board" | "title" | "list" | "due" | "completed" | `field:${string}`;

interface Row {
  card: BoardCard;
  listId: string;
  listTitle: string;
  /** Board order: list position, then card position — the default sort. */
  order: number;
}

interface TableViewProps {
  board: BoardDetail;
  /** Does this card pass the board's search + filters? */
  cardShown: (card: BoardCard) => boolean;
  canEdit: boolean;
  me: AuthMeResponse | null;
}

/**
 * The board as one sortable table: a row per card, columns for list, labels,
 * assignees, due date, done and every custom field, with inline editing and
 * multi-select bulk actions. Shares the board's search and filters.
 */
export function TableView({ board, cardShown, canEdit, me }: TableViewProps) {
  const qc = useQueryClient();
  const boardKey = ["board", board.id] as const;
  const [sort, setSort] = useState<{ key: SortKey; dir: 1 | -1 }>({ key: "board", dir: 1 });
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const fields = useMemo(
    () => [...(board.fields ?? [])].sort((a, b) => a.position - b.position),
    [board.fields],
  );
  const lists = useMemo(() => sortLists(board.lists), [board.lists]);
  const setFieldValue = useSetFieldValue(board.id);

  const rows: Row[] = useMemo(() => {
    const out: Row[] = [];
    let order = 0;
    for (const l of lists)
      for (const c of sortCards(l.cards))
        out.push({ card: c, listId: l.id, listTitle: l.title, order: order++ });
    return out;
  }, [lists]);

  const visible = useMemo(() => {
    const shown = rows.filter((r) => cardShown(r.card));
    const value = (r: Row): string | number => {
      switch (sort.key) {
        case "board":
          return r.order;
        case "title":
          return r.card.title.toLowerCase();
        case "list":
          return lists.findIndex((l) => l.id === r.listId);
        case "due":
          // undated cards sort last either way
          return r.card.dueDate ? Date.parse(r.card.dueDate) : sort.dir * Infinity;
        case "completed":
          return r.card.completed ? 1 : 0;
        default: {
          const field = fields.find((f) => `field:${f.id}` === sort.key);
          const v = field ? r.card.fieldValues?.[field.id] : undefined;
          if (v === undefined) return sort.dir === 1 ? "￿" : "";
          if (field?.type === "number") return v as number;
          if (field?.type === "select")
            return field.options.findIndex((o) => o.id === v);
          return field ? formatFieldValue(field, v).toLowerCase() : "";
        }
      }
    };
    return [...shown].sort((a, b) => {
      const va = value(a);
      const vb = value(b);
      if (va < vb) return -sort.dir;
      if (va > vb) return sort.dir;
      return a.order - b.order;
    });
  }, [rows, cardShown, sort, lists, fields]);

  // selection only ever covers cards still on the board and visible
  const visibleIds = new Set(visible.map((r) => r.card.id));
  const selectedIds = [...selected].filter((id) => visibleIds.has(id));
  const allSelected = visible.length > 0 && selectedIds.length === visible.length;

  const invalidate = () => qc.invalidateQueries({ queryKey: boardKey });

  /** Run one request per selected card, then refresh once and report. */
  async function bulk(label: string, fn: (cardId: string) => Promise<unknown>) {
    const ids = selectedIds;
    const results = await Promise.allSettled(ids.map((id) => fn(id)));
    const failed = results.filter((r) => r.status === "rejected").length;
    invalidate();
    toast(
      failed
        ? `${label}: ${ids.length - failed} done, ${failed} failed`
        : `${label}: ${ids.length} ${ids.length === 1 ? "card" : "cards"}`,
    );
  }

  async function updateOne(cardId: string, patch: Parameters<typeof api.cards.update>[1]) {
    try {
      await api.cards.update(cardId, patch);
    } finally {
      invalidate();
    }
  }
  async function moveOne(cardId: string, listId: string) {
    try {
      await api.cards.move(cardId, { listId });
    } finally {
      invalidate();
    }
  }

  function toggleSort(key: SortKey) {
    setSort((s) => (s.key === key ? { key, dir: s.dir === 1 ? -1 : 1 } : { key, dir: 1 }));
  }

  // a render helper, not a component: a component defined in render would
  // remount (and drop focus) on every re-render
  const header = (k: SortKey, children: React.ReactNode, className = "") => (
    <th
      key={k}
      aria-sort={sort.key === k ? (sort.dir === 1 ? "ascending" : "descending") : undefined}
      className={`whitespace-nowrap px-2 py-2 text-left font-medium ${className}`}
    >
      <button
        type="button"
        onClick={() => toggleSort(k)}
        className="inline-flex items-center gap-1 hover:text-slate-900"
      >
        {children}
        {sort.key === k && (sort.dir === 1 ? <ArrowUp size={12} /> : <ArrowDown size={12} />)}
      </button>
    </th>
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col p-4">
      {canEdit && selectedIds.length > 0 && (
        <BulkBar
          board={board}
          lists={lists}
          count={selectedIds.length}
          me={me}
          onClear={() => setSelected(new Set())}
          onMove={(listId) => bulk("Moved", (id) => api.cards.move(id, { listId }))}
          onAddLabel={(labelId) => bulk("Labelled", (id) => api.labels.assign(id, labelId))}
          onRemoveLabel={(labelId) => bulk("Unlabelled", (id) => api.labels.unassign(id, labelId))}
          onAssignMe={() => me && bulk("Assigned", (id) => api.assignees.assign(id, me.id))}
          onDue={(due) => bulk("Due date set", (id) => api.cards.update(id, { dueDate: due }))}
          onComplete={(completed) =>
            bulk(completed ? "Completed" : "Reopened", (id) => api.cards.update(id, { completed }))
          }
          onArchive={async () => {
            await bulk("Archived", (id) => api.cards.archive(id));
            setSelected(new Set());
          }}
        />
      )}
      <div className="themed-scroll min-h-0 flex-1 overflow-auto rounded-lg border border-slate-200 bg-[var(--surface)]">
        <table className="w-full border-collapse text-sm">
          <thead className="sticky top-0 z-10 bg-[var(--surface)] text-xs text-slate-500 shadow-[0_1px_0_var(--tw-shadow-color)] shadow-slate-200">
            <tr>
              {canEdit && (
                <th className="w-8 px-2 py-2">
                  <input
                    type="checkbox"
                    aria-label="Select all"
                    checked={allSelected}
                    onChange={(e) =>
                      setSelected(e.target.checked ? new Set(visible.map((r) => r.card.id)) : new Set())
                    }
                  />
                </th>
              )}
              {header("title", "Card", "min-w-[16rem]")}
              {header("list", "List")}
              <th className="px-2 py-2 text-left font-medium">Labels</th>
              <th className="px-2 py-2 text-left font-medium">Assignees</th>
              {header("due", "Due")}
              {header("completed", "Done")}
              {fields.map((f) => header(`field:${f.id}`, f.name, "min-w-[9rem]"))}
            </tr>
          </thead>
          <tbody>
            {visible.map(({ card, listId }) => (
              <tr
                key={card.id}
                className={`border-t border-slate-100 align-middle ${
                  selected.has(card.id) ? "bg-blue-50" : "hover:bg-slate-50"
                }`}
              >
                {canEdit && (
                  <td className="px-2 py-1.5">
                    <input
                      type="checkbox"
                      aria-label={`Select ${card.title}`}
                      checked={selected.has(card.id)}
                      onChange={(e) =>
                        setSelected((s) => {
                          const next = new Set(s);
                          if (e.target.checked) next.add(card.id);
                          else next.delete(card.id);
                          return next;
                        })
                      }
                    />
                  </td>
                )}
                <td className="px-2 py-1.5">
                  <Link
                    to={`/boards/${board.id}/cards/${card.id}`}
                    className={`font-medium hover:underline ${
                      card.completed ? "text-slate-500 line-through" : "text-slate-900"
                    }`}
                  >
                    {card.title}
                  </Link>
                </td>
                <td className="px-2 py-1.5">
                  <select
                    value={listId}
                    disabled={!canEdit}
                    aria-label={`List for ${card.title}`}
                    onChange={(e) => moveOne(card.id, e.target.value)}
                    className="max-w-[10rem] rounded border border-transparent bg-transparent px-1 py-0.5 hover:border-slate-300 disabled:opacity-100"
                  >
                    {lists.map((l) => (
                      <option key={l.id} value={l.id}>
                        {l.title}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="px-2 py-1.5">
                  <div className="flex min-w-[6rem] flex-wrap gap-1 whitespace-nowrap">
                    {card.labels.map((l) => (
                      <LabelChip key={l.id} label={l} compact />
                    ))}
                  </div>
                </td>
                <td className="px-2 py-1.5">
                  <div className="flex gap-0.5">
                    {card.assignees.map((a) => (
                      <Avatar
                        key={a.userId}
                        userId={a.userId}
                        name={a.displayName || a.username}
                        title={`@${a.username}`}
                      />
                    ))}
                  </div>
                </td>
                <td className="w-36 px-2 py-1.5">
                  <CommitInput
                    type="date"
                    value={card.dueDate ? toDateInputValue(card.dueDate) : ""}
                    disabled={!canEdit}
                    ariaLabel={`Due date for ${card.title}`}
                    onCommit={(v) => updateOne(card.id, { dueDate: v ? fromDateInputValue(v) : null })}
                  />
                </td>
                <td className="px-2 py-1.5 text-center">
                  <input
                    type="checkbox"
                    checked={card.completed}
                    disabled={!canEdit}
                    aria-label={`Done: ${card.title}`}
                    onChange={(e) => updateOne(card.id, { completed: e.target.checked })}
                  />
                </td>
                {fields.map((f) => (
                  <td key={f.id} className="px-2 py-1.5">
                    <FieldEditor
                      field={f}
                      value={card.fieldValues?.[f.id]}
                      canEdit={canEdit}
                      onChange={(value) =>
                        setFieldValue.mutate({ cardId: card.id, fieldId: f.id, value })
                      }
                    />
                  </td>
                ))}
              </tr>
            ))}
            {visible.length === 0 && (
              <tr>
                <td colSpan={7 + fields.length} className="px-3 py-6 text-center text-slate-500">
                  {rows.length ? "No cards match the current search or filters." : "No cards yet."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function BulkBar({
  board,
  lists,
  count,
  me,
  onClear,
  onMove,
  onAddLabel,
  onRemoveLabel,
  onAssignMe,
  onDue,
  onComplete,
  onArchive,
}: {
  board: BoardDetail;
  lists: BoardDetail["lists"];
  count: number;
  me: AuthMeResponse | null;
  onClear: () => void;
  onMove: (listId: string) => void;
  onAddLabel: (labelId: string) => void;
  onRemoveLabel: (labelId: string) => void;
  onAssignMe: () => void;
  onDue: (iso: string | null) => void;
  onComplete: (completed: boolean) => void;
  onArchive: () => void;
}) {
  const [due, setDue] = useState("");
  const btn = "rounded bg-slate-100 px-2 py-1 text-slate-700 hover:bg-slate-200";
  const sel = "rounded border border-slate-300 bg-[var(--surface)] px-1.5 py-1";
  // a select used as a one-shot action menu: act, then reset to the prompt
  const actionSelect = (
    label: string,
    options: { value: string; label: string }[],
    act: (v: string) => void,
  ) => (
    <select
      value=""
      aria-label={label}
      onChange={(e) => e.target.value && act(e.target.value)}
      className={sel}
    >
      <option value="">{label}…</option>
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
  const labelOptions = [...board.labels]
    .sort((a, b) => a.position - b.position)
    .map((l) => ({ value: l.id, label: l.name || l.color }));
  return (
    <div
      role="toolbar"
      aria-label="Bulk actions"
      className="mb-3 flex flex-wrap items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm"
    >
      <span className="font-medium text-blue-800">
        {count} selected
      </span>
      <button type="button" onClick={onClear} aria-label="Clear selection" className="rounded p-1 text-blue-700 hover:bg-blue-100">
        <X size={14} />
      </button>
      <span className="mx-1 h-5 w-px bg-blue-200" />
      {actionSelect(
        "Move to",
        lists.map((l) => ({ value: l.id, label: l.title })),
        onMove,
      )}
      {labelOptions.length > 0 && actionSelect("Add label", labelOptions, onAddLabel)}
      {labelOptions.length > 0 && actionSelect("Remove label", labelOptions, onRemoveLabel)}
      {me && (
        <button type="button" onClick={onAssignMe} className={btn}>
          Assign to me
        </button>
      )}
      <span className="inline-flex items-center gap-1">
        <input
          type="date"
          value={due}
          onChange={(e) => setDue(e.target.value)}
          aria-label="Due date for selected cards"
          className={sel}
        />
        <button
          type="button"
          onClick={() => onDue(due ? fromDateInputValue(due) : null)}
          className={btn}
        >
          {due ? "Set due" : "Clear due"}
        </button>
      </span>
      <button type="button" onClick={() => onComplete(true)} className={btn}>
        Mark done
      </button>
      <button type="button" onClick={() => onComplete(false)} className={btn}>
        Reopen
      </button>
      <ConfirmButton
        onConfirm={onArchive}
        title="Archive the selected cards"
        className={btn}
        confirmClassName="rounded bg-red-600 px-2 py-1 text-white"
        confirmLabel={`Archive ${count}?`}
      >
        Archive
      </ConfirmButton>
    </div>
  );
}

